// src/config/authConfig.ts

import type { Configuration } from "@azure/msal-browser";
import { LogLevel } from "@azure/msal-browser";
import { createLogger } from "../utils/logger";
import { track } from "../utils/analytics";

const msalLog = createLogger("MSAL");

/** Azure AD App Registration client ID. Env-driven so rotation to a new app
 * registration is a config change, not a code change. Default preserves the
 * current CS-AzureLocal client ID for backward compat with existing deploys. */
export const AZURE_CLIENT_ID =
  process.env.REACT_APP_AZURE_CLIENT_ID || "9150f52b-9068-4f67-a897-48f09b6b23c6";

/**
 * Runtime kill-switch for NAA. When true, `detectAuthTier` skips the
 * NestedAppAuth check and returns `legacy_sso` (or `none` if Legacy SSO is
 * also unsupported). Set `REACT_APP_FORCE_LEGACY_SSO=true` to roll back the
 * NAA tier without a code change — useful if MSAL v5 has a regression or
 * Azure AD has a broker outage.
 */
export const FORCE_LEGACY_SSO = process.env.REACT_APP_FORCE_LEGACY_SSO === "true";

/**
 * Storage key for the user's Azure tenant id. After first successful auth we
 * pin the MSAL `authority` to `https://login.microsoftonline.com/{tid}` on
 * subsequent loads instead of the slower `/common` discovery path.
 */
const TENANT_STORAGE_KEY = "cs-azure-tenant-id";

/** Read a persisted tenant id, or null if not yet captured. */
export function getPersistedTenantId(): string | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist the tenant id so future MSAL inits can use a tenant-pinned authority. */
export function persistTenantId(tid: string): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(TENANT_STORAGE_KEY, tid);
  } catch {
    /* storage errors are non-fatal — we fall back to /common on next load */
  }
}

/**
 * Base URL of the add-in server. Used to derive both the Application ID URI and
 * the MSAL redirect URI. Env-driven so it resolves to the correct add-in server
 * domain in all deployment contexts — in particular SharePoint App Catalog
 * deployments where window.location.origin returns the SharePoint tenant domain
 * (e.g. dandilionllc-my.sharepoint.com) rather than the add-in server.
 *
 * Set REACT_APP_AZURE_BASE_URL per environment:
 *   local:  https://localhost:3000  (default)
 *   dev:    https://dev.contract.space
 *   prod:   https://prod.contract.space
 */
export const AZURE_BASE_URL = process.env.REACT_APP_AZURE_BASE_URL || "https://localhost:3000";

const hostForAppId = (() => {
  try {
    return new URL(AZURE_BASE_URL).host;
  } catch {
    return "localhost:3000";
  }
})();

/**
 * The app's Application ID URI, exactly as set in Azure AD → Expose an API.
 * Must be registered per-host in Azure AD (e.g. `api://localhost:3000/<clientId>` for
 * local dev, `api://dev.contract.space/<clientId>` for DEV, etc.).
 * Mismatch between the scope we request and what's registered causes AAD to treat
 * the scope as unknown and hangs silent token acquisition.
 *
 * Also must match `<WebApplicationInfo><Resource>` in the deployed manifest.xml
 * (Legacy SSO path). The webpack `CopyWebpackPlugin` transform rewrites the
 * manifest's `api://localhost:3000/` prefix to `api://<target-host>/` at build time.
 */
export const API_IDENTIFIER_URI = `api://${hostForAppId}/${AZURE_CLIENT_ID}`;

/** The host-based custom API scope. This is the Application ID URI form that the
 * Legacy SSO manifest `<WebApplicationInfo><Resource>` depends on (see the manifest
 * regression test) — keep it host-based even though NAA no longer requests it. */
export const API_SCOPE = `${API_IDENTIFIER_URI}/access_as_user`;

/**
 * Scope requested for NAA (MSAL) token acquisition — uses the **GUID-based**
 * resource identifier (`<clientId>/access_as_user`), not the host-based
 * Application ID URI.
 *
 * Why: on Word for Windows desktop, NAA is brokered by the Windows token broker
 * (WAM) through Azure's v1 token endpoint, which rejects an app requesting a token
 * for itself unless the resource is named by its GUID-based App Identifier (error
 * AADSTS90009). The host-based `API_SCOPE` works on the browser (v2) path used by
 * Mac/web but fails on the Windows WAM path. The bare client-id GUID is a
 * GUID-based identifier and needs no additional Application ID URI registration.
 * `API_IDENTIFIER_URI` stays host-based (Legacy SSO manifest contract); the issued
 * token is unchanged either way (v2 → `aud` = clientId GUID, `scp` = access_as_user).
 */
export const NAA_SCOPE = `${AZURE_CLIENT_ID}/access_as_user`;

/** Scopes requested during MSAL (NAA) token acquisition */
export const MSAL_SCOPES = [NAA_SCOPE];

/** Timeout for ssoSilent() / acquireTokenSilent(). MSAL v5's iframe-based
 * silent auth completes in ~500ms when the bridge is working; MSAL's internal
 * `iframeBridgeTimeout` is 10s. We wrap at 15s so a slow-but-legitimate cold
 * AAD cache + conditional-access policy eval (often 10-11s total) doesn't get
 * mis-reported as a timeout, while still breaking out of "bridge has no
 * listener" scenarios (e.g. user reloaded mid-auth) faster than the 20s we
 * used previously. 5s buffer over MSAL's internal 10s. */
export const SSO_SILENT_TIMEOUT_MS = 15000;

/** Total per-attempt timeout for Legacy SSO (Office.auth.getAccessToken). Shorter
 * than the NAA wrapper because the Office host resolves its own silent SSO
 * path locally — there's no hidden iframe to AAD. Retries (with backoff) live
 * in AuthContext. */
export const LEGACY_SSO_TIMEOUT_MS = 10000;

/**
 * MSAL v5 redirect-bridge URL. The bridge is a minimal HTML page (`src/auth/auth.html`)
 * built as a separate webpack entry. MSAL's hidden silent-auth iframe redirects here
 * after AAD issues the code; the bridge calls `broadcastResponseToMainFrame()` which
 * posts the response back to the main window via BroadcastChannel.
 *
 * The path is injected by webpack's DefinePlugin (`process.env.AUTH_BRIDGE_PATH`)
 * so it honors the configured `publicPath`:
 *   - local dev: `/auth.html`
 *   - deployed (e.g. ALB routing `/add-in/*` → ECS): `/add-in/auth.html`
 *
 * The URL must match an SPA redirect URI registered in Azure AD for the target
 * host. Registered for dev: `https://localhost:3000/auth.html`. For prod, the
 * full URL (e.g. `https://contract.space/add-in/auth.html`) must be registered
 * before deploy.
 */
// Guard against webpack misconfiguration: if the DefinePlugin entry is ever
// dropped, NODE_ENV=production builds would silently ship the wrong path
// (`/auth.html` at root when assets are actually under `/add-in/`). Surface
// it loudly instead of letting silent auth break in prod.
if (
  typeof process !== "undefined" &&
  process.env?.NODE_ENV === "production" &&
  !process.env.AUTH_BRIDGE_PATH
) {
  // eslint-disable-next-line no-console
  console.error(
    "[authConfig] AUTH_BRIDGE_PATH is not set. This indicates webpack's " +
      "DefinePlugin injection is missing. MSAL redirectUri will default to " +
      "`/auth.html`, which is wrong if the app is served under a sub-path."
  );
}
const AUTH_REDIRECT_PATH = process.env.AUTH_BRIDGE_PATH || "/auth.html";
export const AUTH_REDIRECT_URI = `${AZURE_BASE_URL}${AUTH_REDIRECT_PATH}`;

/**
 * MSAL internal log level. Verbose in dev so we can see the broker handshake
 * during debugging; Warning in prod to avoid the CPU cost of the library's
 * internal verbose logger callbacks firing for every operation. Our own logger
 * suppresses INFO in prod independently, but MSAL still builds the log strings
 * before calling our callback — this constant short-circuits that.
 */
const isDevEnvironment =
  typeof window !== "undefined" &&
  (window.location?.hostname === "localhost" || window.location?.hostname === "127.0.0.1");
const msalLogLevel = isDevEnvironment ? LogLevel.Verbose : LogLevel.Warning;

/**
 * MSAL `authority`. Defaults to `/common` (works for any tenant the user may
 * sign in with). Once we've captured the user's tid from their first
 * successful token, subsequent loads use `/{tid}` for faster AAD discovery
 * (skips the tenant-resolution step).
 */
function buildAuthority(): string {
  const tid = getPersistedTenantId();
  return tid
    ? `https://login.microsoftonline.com/${tid}`
    : "https://login.microsoftonline.com/common";
}

/** MSAL configuration for NAA */
export const MSAL_CONFIG: Configuration = {
  auth: {
    clientId: AZURE_CLIENT_ID,
    authority: buildAuthority(),
    redirectUri: AUTH_REDIRECT_URI,
  },
  cache: {
    // localStorage is required so the hidden iframe used by ssoSilent and the
    // main window share the same MSAL token cache. With the default
    // sessionStorage they're isolated, the main window never sees the token
    // the iframe wrote, and ssoSilent times out at MSAL's internal 10s limit.
    // Microsoft's official NAA sample uses the same setting for this reason.
    cacheLocation: "localStorage",
  },
  system: {
    loggerOptions: {
      logLevel: msalLogLevel,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) {
          msalLog.error(message);
          // Forward MSAL's own error-level events to PostHog so we can detect
          // internal MSAL regressions (broker handshake failure, token endpoint
          // errors) that don't escalate to a thrown error in our code path.
          // v5 obfuscates the message into short codes (e.g. "12jy9a") — still
          // useful as a fingerprint for aggregation.
          track("auth:msal_internal_error", { message });
        } else if (level === LogLevel.Warning) {
          msalLog.warn(message);
        } else {
          msalLog.info(message);
        }
      },
    },
  },
};

// eslint-disable-next-line no-console
console.log("[authConfig] MSAL config", {
  clientId: AZURE_CLIENT_ID,
  authority: MSAL_CONFIG.auth.authority,
  redirectUri: AUTH_REDIRECT_URI,
  scope: NAA_SCOPE,
  baseUrl: AZURE_BASE_URL,
  forceLegacySso: FORCE_LEGACY_SSO,
  cacheLocation: MSAL_CONFIG.cache?.cacheLocation,
});

/** Auth tier type for telemetry and internal branching */
export type AuthTier = "naa" | "legacy_sso" | "none";
